'use client';

import { Popover, PopoverContent, PopoverTrigger } from '@heroui/react';
import type { IAccessLevel } from '@quesmed/types/models';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { type IMenuList, isHeader } from '@/config';
import { useDisclosure } from '@/core/hooks';
import { Accordion, cn, Tooltip } from '@/core/ui';

export interface IProps {
  menu: IMenuList;
  level: number;
  accessLevel: IAccessLevel;
  collapsed?: boolean;
}

const CollapsedHeaderMenuItem = ({
  menu,
  accessLevel,
  pathname,
  level,
  isActive,
}: {
  menu: Extract<IMenuList, { items: IMenuList[] }>;
  accessLevel: IAccessLevel;
  pathname: string;
  level: number;
  isActive: boolean;
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsPopoverOpen(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsPopoverOpen(false);
    }, 150); // Small delay to allow moving to popover
  };

  return (
    <Popover
      placement='right-start'
      offset={10}
      showArrow
      isOpen={isPopoverOpen}
      onOpenChange={setIsPopoverOpen}
      triggerType='dialog'
    >
      <PopoverTrigger>
        <button
          type='button'
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={cn(
            'flex flex-row justify-center items-center',
            'px-3 py-3.5 rounded-md text-default-800',
            'cursor-pointer w-full',
            {
              'bg-primary-400 text-primary-900': isActive && !level,
              'hover:bg-default-100': !isActive,
            },
          )}
        >
          <menu.icon className='md:w-6 md:h-6 w-5 h-5' />
        </button>
      </PopoverTrigger>
      <PopoverContent className='p-2' onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <div className='flex flex-col min-w-[200px]'>
          <div className='px-3 py-2 border-b border-default-200 mb-1'>
            <p className='font-semibold text-sm capitalize text-foreground'>{menu.label}</p>
          </div>
          <div className='flex flex-col'>
            {menu.items.map((subMenu) => (
              <SubMenuItem
                key={subMenu.label}
                subMenu={subMenu}
                accessLevel={accessLevel}
                pathname={pathname}
              />
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const SubMenuItem = ({
  subMenu,
  accessLevel,
  pathname,
}: {
  subMenu: IMenuList;
  accessLevel: IAccessLevel;
  pathname: string;
}) => {
  // Early return for headers or items without access
  if (!subMenu.accessLevels.includes(accessLevel) || isHeader(subMenu)) {
    return null;
  }

  // Type guard: subMenu is IMenuListItem at this point
  const menuItem = subMenu as Extract<IMenuList, { href: string }>;

  // Calculate active state directly (no hook needed)
  const match = matchPath(menuItem.href, pathname);
  const subMenuIsActive = !!match;

  const subMenuHref = menuItem.enabled
    ? menuItem.href?.includes(':id')
      ? menuItem.href.replace(':id', 'null')
      : menuItem.href
    : '#';

  return (
    <Link
      to={subMenuHref}
      className={cn(
        'flex flex-row space-x-3 items-center',
        'px-3 py-2.5 rounded-md text-foreground',
        'cursor-pointer transition-colors',
        {
          'bg-primary-400 text-primary-900': subMenuIsActive,
          'hover:bg-default-100': !subMenuIsActive,
          'cursor-not-allowed opacity-50': !menuItem.enabled,
        },
      )}
    >
      <menuItem.icon className='w-5 h-5' />
      <span className='capitalize text-sm'>{menuItem.label}</span>
    </Link>
  );
};

export const MenuItem = ({ menu, accessLevel, level, collapsed = false }: IProps) => {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const pathname = useLocation().pathname;
  const leftPadding = 8;

  useEffect(() => {
    if (pathname.toLowerCase().includes(menu.label.toLowerCase())) {
      onOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isActive = useMemo(() => {
    if (isHeader(menu)) {
      return pathname.toLowerCase().includes(menu.label.toLowerCase());
    }

    const match = matchPath(menu.href, pathname);
    return !!match;
  }, [pathname, menu]);
  if (!menu.accessLevels.includes(accessLevel)) {
    return null;
  }

  if (isHeader(menu)) {
    if (collapsed) {
      return (
        <CollapsedHeaderMenuItem
          menu={menu}
          accessLevel={accessLevel}
          pathname={pathname}
          level={level}
          isActive={isActive}
        />
      );
    }

    return (
      <Accordion
        open={isOpen}
        onToggle={onOpenChange}
        style={{ marginLeft: `${level * leftPadding}px` }}
        className={cn(
          'flex flex-row justify-between items-center',
          'px-3 py-3.5 rounded-md text-default-800',
          'cursor-pointer',
          {
            'bg-primary-400 text-primary-900': isActive && !level,
            'bg-default-100': isOpen && !isActive,
            'hover:bg-default-100 ': !isActive && !isOpen,
          },
        )}
        title={
          <div className={cn('flex flex-row space-x-4 items-center')} key={menu.label}>
            <menu.icon className='md:w-6 md:h-6 w-5 h-5' />
            <span className='capitalize text-sm md:text-base'>{menu.label}</span>
          </div>
        }
      >
        <div className='py-1 ml-6 my-1 border-l-2 border-foreground-200'>
          {menu.items.map((menu) => (
            <MenuItem
              accessLevel={accessLevel}
              key={menu.label}
              menu={menu}
              level={level + 1}
              collapsed={collapsed}
            />
          ))}
        </div>
      </Accordion>
    );
  }

  if (collapsed) {
    return (
      <Tooltip content={menu.tooltip || menu.label} placement='right' showArrow isDisabled={!menu.enabled}>
        <Link
          to={
            menu.enabled ? (menu.href?.includes(':id') ? menu.href.replace(':id', 'null') : menu.href) : '#'
          }
          className={cn(
            'flex flex-row justify-center items-center',
            'px-3 rounded-md text-foreground',
            'cursor-pointer w-full',
            {
              'py-2.5': level,
              'py-3.5': !level,
              'bg-primary-400 text-primary-900': isActive && !level,
              'text-primary-800 font-medium': isActive && level,
              'hover:bg-default-100': !isActive,
              'cursor-not-allowed opacity-50': !menu.enabled,
            },
          )}
          key={menu.label}
        >
          <menu.icon className='md:w-6 md:h-6 w-5 h-5' />
        </Link>
      </Tooltip>
    );
  }

  return (
    <Link
      to={menu.enabled ? (menu.href?.includes(':id') ? menu.href.replace(':id', 'null') : menu.href) : '#'}
      className={cn(
        'flex flex-row space-x-4 items-start',
        'px-3 rounded-md text-foreground',
        'cursor-pointer',
        {
          'py-2.5': level,
          'py-3.5': !level,
          'bg-primary-400 text-primary-900': isActive && !level,
          'text-primary-800 font-medium': isActive && level,
          'hover:bg-default-100': !isActive,
          'cursor-not-allowed opacity-50': !menu.enabled,
          // "hover:bg-default-50": !isActive && level,
        },
      )}
      style={{ marginLeft: `${level * leftPadding}px` }}
      key={menu.label}
    >
      <menu.icon className='md:w-6 md:h-6 w-5 h-5' />
      <Tooltip
        // offset={100}
        showArrow
        isDisabled={menu.enabled}
        placement='right'
        color='default'
        content={menu.tooltip || 'It will be available soon!'}
      >
        <span className='capitalize text-sm md:text-base'>{menu.label}</span>
      </Tooltip>
    </Link>
  );
};
